import type { Breadcrumb, BreadcrumbHint, ErrorEvent, EventHint } from '@sentry/core';
import { isString } from 'lodash';

import { parseJson } from 'src/lib/common';
import * as env from 'src/lib/env';

import { eventHasError, exceptionIncludesValue } from './utils';

/**
 * Transform event before sending to Sentry
 * @param event - event
 * @param hint - hint
 * @returns transformed event
 */
export const transformEventBeforeSend = (event: ErrorEvent, hint: EventHint): ErrorEvent => {
  // Transform some known non-critical errors to "info" level
  if (includesKnownNonCriticalErrorMessage(event)) {
    event.level = 'info';
    event.extra = {
      ...event.extra,
      original_level: event.level,
    };

    return event;
  }

  // Transform some known GoogleTagManager errors to "info" level
  if (includesKnownNonCriticalGTMError(event)) {
    event.level = 'info';
    event.tags = {
      ...event.tags,
      original_level: event.level,
      transaction: 'GTM',
    };

    return event;
  }

  // Transform some known Slate errors to "info" level
  if (includesKnownNonCriticalSlateError(event)) {
    event.level = 'info';
    event.tags = {
      ...event.tags,
      original_level: event.level,
      transaction: 'Slate',
    };

    return event;
  }

  // Add the syntheticException when it's a critical error for more information
  event.extra = {
    ...event.extra,
    original_exception: hint?.syntheticException,
  };

  return event;
};

/**
 * Transform breadcrumb before sending to Sentry
 * @param breadcrumb - breadcrumb
 * @param hint - hint
 * @return transformed breadcrumb
 */
export const transformBreadcrumbBeforeSend = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint
): Breadcrumb => {
  if (breadcrumb.category === 'ui.click' && hint?.event) {
    const { path } = hint.event;
    breadcrumb.message = createSentryPath(path);
  }

  if (breadcrumb.category === 'fetch') {
    const graphqlData = getGraphQLBreadcrumbData(breadcrumb, hint);
    if (graphqlData) {
      return {
        ...breadcrumb,
        category: 'graphql',
        data: {
          ...breadcrumb.data,
          ...graphqlData,
        },
      };
    }
  }

  return breadcrumb;
};

const KNOWN_NON_CRITICAL_ERROR_MESSAGES = [
  'Non-Error promise rejection captured with',
  // We often get these from browser extensions, like "Grammarly"
  'ResizeObserver loop',
  // This might be triggered by Apollo, when user closes the tab before the page is fully loaded
  'AbortError: The user aborted a request.',
  // These come from inside of `apollo-upload-client`, when internal AbortController gets aborted as a side-effect of the request controller being aborted
  'signal is aborted without reason',
];

const includesKnownNonCriticalErrorMessage = (event: ErrorEvent): boolean =>
  KNOWN_NON_CRITICAL_ERROR_MESSAGES.some((message) => exceptionIncludesValue(event, message));

const includesKnownNonCriticalGTMError = (event: ErrorEvent): boolean =>
  eventHasError(event, {
    type: 'QuotaExceededError',
    path: 'googletagmanager.com',
  });

const includesKnownNonCriticalSlateError = (event: ErrorEvent): boolean =>
  eventHasError(event, {
    value: 'Cannot resolve a DOM point from Slate point',
  }) ||
  eventHasError(event, {
    value: 'Cannot resolve a Slate point from DOM point',
  }) ||
  eventHasError(event, {
    value: 'Cannot resolve a Slate node from DOM node',
  }) ||
  eventHasError(event, {
    value: "Failed to execute 'setEnd' on 'Range'",
    path: 'slate-react',
  }) ||
  eventHasError(event, {
    value: 'Unable to find DocumentOrShadowRoot',
    path: 'slate-react',
  });

const mapTagName = (element: Element): string => {
  if (!element) {
    return '?';
  }

  const { tagName } = element;

  if (element instanceof HTMLElement && element.dataset) {
    const datasetData =
      element.dataset.sentryId || element.dataset.testid || element.dataset.onboarding;
    if (datasetData) {
      return `${tagName}[${datasetData}]`;
    }
  }

  if (element.id) {
    return `${tagName}#${element.id}`;
  }

  if (element.className) {
    return `${tagName}.${element.className}`;
  }

  return tagName;
};

const createSentryPath = (path: Element[]): string =>
  path.map(mapTagName).reverse().splice(2).join(' > ');

interface GraphQLBreadcrumbData {
  operationName: string;
}
const getGraphQLBreadcrumbData = (
  breadcrumb: Breadcrumb,
  hint?: BreadcrumbHint
): GraphQLBreadcrumbData | undefined => {
  const breadcrumbData = breadcrumb?.data;
  const input = hint?.input;
  if (!breadcrumbData || !input) {
    return undefined;
  }

  const [url, inputData] = input;
  if (isString(url) && url.startsWith(env.getBackendUrl())) {
    return parseJson<GraphQLBreadcrumbData>(inputData?.body);
  }

  return undefined;
};
